import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import ScheduleIcon from '@material-ui/icons/Schedule';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import HistoryIcon from '@material-ui/icons/History';
import { Alert } from '@material-ui/lab';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useKubernetesContext } from './KubernetesContext';
import { KubernetesScheduleDialog } from './actions';
import { KubernetesAction, KubernetesActionType } from './actions/types';
import { CRDKubernetesConfig } from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';
import { parseCronExpression, formatCronParts } from './cronUtils';
import { ResponseErrorPanel } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  card: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  cardStart: {
    backgroundColor: '#1a1a1a',
    borderLeft: '4px solid #4CAF50',
    '& .MuiTypography-root': {
      color: '#ffffff',
    },
  },
  cardStop: {
    backgroundColor: '#1a1a1a',
    borderLeft: '4px solid #F44336',
    '& .MuiTypography-root': {
      color: '#ffffff',
    },
  },
  schedulerCard: {
    backgroundColor: '#2a2a2a',
    border: '2px solid #326CE5',
    '& .MuiTypography-root': {
      color: '#ffffff',
    },
  },
  contextHeader: {
    backgroundColor: '#1a1a1a',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: '2px solid #326CE5',
    '& .MuiTypography-root': {
      color: '#64B5F6',
    },
  },
  schedulerStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  scheduleItem: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: theme.shadows[2],
      transform: 'translateY(-2px)',
    },
  },
  scheduleItemStart: {
    backgroundColor: '#263238',
    borderColor: '#4CAF50',
    '& .MuiTypography-root': {
      color: '#ffffff',
    },
  },
  scheduleItemStop: {
    backgroundColor: '#263238',
    borderColor: '#F44336',
    '& .MuiTypography-root': {
      color: '#ffffff',
    },
  },
  scheduleKey: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
    fontSize: '1.1rem',
  },
  cronExpression: {
    fontFamily: 'monospace',
    backgroundColor: '#424242',
    color: '#00E676',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
  },
  cronDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  cronParts: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
}));

interface Schedule {
  key: string;
  value: string;
  type: 'start' | 'stop';
}

interface KubernetesScheduleManagerProps {
  entityRef: string;
  kubernetesConfig: CRDKubernetesConfig;
}

export const KubernetesScheduleManager: React.FC<
  KubernetesScheduleManagerProps
> = ({ entityRef, kubernetesConfig }) => {
  const classes = useStyles();
  const { api, projectCode } = useKubernetesContext();
  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<KubernetesActionType | null>(null);
  const [dialogScheduleType, setDialogScheduleType] = useState<
    'start' | 'stop'
  >('start');
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null,
  );
  const [isSchedulerEnabled, setIsSchedulerEnabled] = useState(true);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [lastExecution, setLastExecution] = useState<{
    start: string;
    stop: string;
  }>({ start: '', stop: '' });

  const {
    value: namespaceAnnotations,
    loading: isLoading,
    error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return null;
    try {
      const response = await api.getKubernetesNamespaceAnnotations({
        serviceType: 'kubernetes',
        resourceType: 'resources',
        projectCode: projectCode,
        entityRef: entityRef,
        cloudConfig: kubernetesConfig,
      });
      return response.response;
    } catch (err) {
      console.error('Failed to fetch namespace annotations:', err);
      throw err;
    }
  }, [projectCode, api, kubernetesConfig, entityRef]);

  const handleRefresh = useCallback(() => {
    // Invalidate cache for Kubernetes namespace annotations
    api.invalidateCache('getKubernetesNamespaceAnnotations');
    refresh();
  }, [api, refresh]);

  useEffect(() => {
    if (namespaceAnnotations?.annotations) {
      const isEnabled =
        namespaceAnnotations.annotations['cloudstart/disabled'] === 'false';
      setIsSchedulerEnabled(isEnabled);

      const scheduleList: Schedule[] = [];
      let startLastExecutionDate = '';
      let stopLastExecutionDate = '';

      // Pattern: starts with cloudstart/{start|stop}, followed optionally by -{numeric}, but nothing else
      const isValidScheduleKey = (key: string, type: 'start' | 'stop') => {
        const pattern = new RegExp(`^cloudstart/${type}(-[0-9]+)?$`);
        return pattern.test(key);
      };

      Object.entries(namespaceAnnotations.annotations).forEach(
        ([key, value]) => {
          if (key === 'cloudstart/start-last-execution-date') {
            startLastExecutionDate = value as string;
          } else if (key === 'cloudstart/stop-last-execution-date') {
            stopLastExecutionDate = value as string;
          } else if (key.startsWith('cloudstart/start')) {
            if (isValidScheduleKey(key, 'start')) {
              scheduleList.push({
                key,
                value: value as string,
                type: 'start',
              });
            }
          } else if (key.startsWith('cloudstart/stop')) {
            if (isValidScheduleKey(key, 'stop')) {
              scheduleList.push({
                key,
                value: value as string,
                type: 'stop',
              });
            }
          }
        },
      );
      setSchedules(scheduleList);
      setLastExecution({
        start: startLastExecutionDate,
        stop: stopLastExecutionDate,
      });
    }
  }, [namespaceAnnotations]);

  const handleToggleScheduler = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    setConfirmDialogOpen(false);
    setIsActionInProgress(true);
    try {
      const action = isSchedulerEnabled
        ? KubernetesAction.DISABLE_SCHEDULER
        : KubernetesAction.ENABLE_SCHEDULER;

      const response = await api.executeAction({
        serviceType: 'kubernetes',
        entityRef,
        kubernetes: {
          action,
          resourceType: 'resources',
          kubeContext: kubernetesConfig.kubeContext,
          projectCode,
        },
      });

      if (response.status === 'success') {
        alertApi.post({
          message: `Scheduler ${
            isSchedulerEnabled ? 'disabled' : 'enabled'
          } successfully`,
          severity: 'success',
        });
        handleRefresh();
      } else {
        throw new Error(response.error || 'Unknown error');
      }
    } catch (err) {
      errorApi.post(new Error('Failed to toggle scheduler'));
    } finally {
      setIsActionInProgress(false);
    }
  }, [
    isSchedulerEnabled,
    api,
    entityRef,
    kubernetesConfig,
    projectCode,
    alertApi,
    handleRefresh,
    errorApi,
  ]);

  const handleCancelToggle = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const handleOpenScheduleDialog = useCallback(
    (
      action: KubernetesActionType,
      scheduleType: 'start' | 'stop' = 'start',
      schedule?: Schedule,
    ) => {
      setSelectedAction(action);
      setDialogScheduleType(scheduleType);
      setSelectedSchedule(schedule || null);
      setScheduleDialogOpen(true);
    },
    [],
  );

  const handleScheduleConfirm = useCallback(
    async (scheduleKey: string, scheduleValue: string) => {
      if (!selectedAction) return;

      setScheduleDialogOpen(false);
      setIsActionInProgress(true);

      try {
        const response = await api.executeAction({
          serviceType: 'kubernetes',
          entityRef,
          kubernetes: {
            action: selectedAction,
            resourceType: 'resources',
            kubeContext: kubernetesConfig.kubeContext,
            projectCode,
            scheduleKey,
            scheduleValue,
          },
        });

        if (response.status === 'success') {
          const actionMessage =
            selectedAction === KubernetesAction.ADD_SCHEDULER
              ? 'Schedule created successfully'
              : selectedAction === KubernetesAction.UPDATE_SCHEDULER
              ? 'Schedule updated successfully'
              : selectedAction === KubernetesAction.DELETE_SCHEDULER
              ? 'Schedule deleted successfully'
              : 'Schedule action completed successfully';

          alertApi.post({
            message: actionMessage,
            severity: 'success',
          });
          handleRefresh();
        } else {
          throw new Error(response.error || 'Unknown error');
        }
      } catch (err) {
        errorApi.post(new Error('Failed to execute schedule action'));
      } finally {
        setIsActionInProgress(false);
        setSelectedAction(null);
      }
    },
    [
      selectedAction,
      api,
      entityRef,
      kubernetesConfig,
      projectCode,
      alertApi,
      handleRefresh,
      errorApi,
    ],
  );

  const getSchedulesByType = (type: 'start' | 'stop') => {
    return schedules.filter(s => s.type === type);
  };

  if (error) {
    error.message = `Failed to load schedule information`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Schedule Manager
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage start and stop schedules for Kubernetes resources
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card className={`${classes.card} ${classes.schedulerCard}`}>
        <CardContent>
          <Box className={classes.schedulerStatus}>
            <ScheduleIcon fontSize="large" color="primary" />
            <Box style={{ flex: 1 }}>
              <Typography variant="h6">Scheduler Status</Typography>
              <Typography variant="body2" color="textSecondary">
                {isSchedulerEnabled
                  ? 'Scheduler is currently enabled and active'
                  : 'Scheduler is currently disabled'}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isSchedulerEnabled}
                  onChange={handleToggleScheduler}
                  disabled={isActionInProgress || isLoading}
                  color="primary"
                />
              }
              label={isSchedulerEnabled ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Start Schedules */}
        <Grid item xs={12} md={6}>
          <Card className={`${classes.card} ${classes.cardStart}`}>
            <CardContent>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PlayArrowIcon style={{ color: '#4caf50' }} />
                  <Typography variant="h6">Start Schedules</Typography>
                  <Chip
                    label={getSchedulesByType('start').length}
                    size="small"
                    color="primary"
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() =>
                    handleOpenScheduleDialog(
                      KubernetesAction.ADD_SCHEDULER,
                      'start',
                    )
                  }
                  disabled={isActionInProgress || isLoading}
                >
                  Add
                </Button>
              </Box>

              {lastExecution.start && (
                <Box
                  display="flex"
                  alignItems="center"
                  bgcolor="rgba(0, 0, 0, 0.2)"
                  p={1}
                  borderRadius={4}
                  mb={2}
                >
                  <HistoryIcon
                    style={{ fontSize: 20, marginRight: 8, color: '#aaa' }}
                  />
                  <Typography variant="body2" style={{ color: '#fff' }}>
                    Last Execution:{' '}
                    <strong>
                      {new Date(lastExecution.start).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </strong>
                  </Typography>
                </Box>
              )}

              <Divider style={{ marginBottom: 16 }} />

              {isLoading ? (
                <Box style={{ textAlign: 'center', padding: 16 }}>
                  <CircularProgress />
                </Box>
              ) : getSchedulesByType('start').length > 0 ? (
                getSchedulesByType('start').map(schedule => {
                  const cronParts = formatCronParts(schedule.value);
                  const cronDescription = parseCronExpression(schedule.value);
                  return (
                    <Box
                      key={schedule.key}
                      className={`${classes.scheduleItem} ${classes.scheduleItemStart}`}
                    >
                      <Box style={{ flex: 1 }}>
                        <Typography className={classes.scheduleKey}>
                          {schedule.key.replace('cloudstart/', '')}
                        </Typography>
                        <Typography className={classes.cronExpression}>
                          {schedule.value}
                        </Typography>
                        <Typography className={classes.cronDescription}>
                          <AccessTimeIcon fontSize="small" />
                          {cronDescription}
                        </Typography>
                        {cronParts && (
                          <Box className={classes.cronParts}>
                            <Chip
                              size="small"
                              label={`M:${cronParts.minute}`}
                              style={{
                                backgroundColor: '#1565C0',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`H:${cronParts.hour}`}
                              style={{
                                backgroundColor: '#6A1B9A',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`D:${cronParts.dayOfMonth}`}
                              style={{
                                backgroundColor: '#EF6C00',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`Mo:${cronParts.month}`}
                              style={{
                                backgroundColor: '#00695C',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`W:${cronParts.dayOfWeek}`}
                              style={{
                                backgroundColor: '#AD1457',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Box className={classes.actions}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenScheduleDialog(
                                KubernetesAction.UPDATE_SCHEDULER,
                                'start',
                                schedule,
                              )
                            }
                            disabled={isActionInProgress}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenScheduleDialog(
                                KubernetesAction.DELETE_SCHEDULER,
                                'start',
                                schedule,
                              )
                            }
                            disabled={isActionInProgress}
                          >
                            <DeleteIcon fontSize="small" color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box className={classes.emptyState}>
                  <Typography variant="body2">
                    No start schedules configured
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Stop Schedules */}
        <Grid item xs={12} md={6}>
          <Card className={`${classes.card} ${classes.cardStop}`}>
            <CardContent>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <StopIcon style={{ color: '#f44336' }} />
                  <Typography variant="h6">Stop Schedules</Typography>
                  <Chip
                    label={getSchedulesByType('stop').length}
                    size="small"
                    color="secondary"
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() =>
                    handleOpenScheduleDialog(
                      KubernetesAction.ADD_SCHEDULER,
                      'stop',
                    )
                  }
                  disabled={isActionInProgress || isLoading}
                >
                  Add
                </Button>
              </Box>

              {lastExecution.stop && (
                <Box
                  display="flex"
                  alignItems="center"
                  bgcolor="rgba(0, 0, 0, 0.2)"
                  p={1}
                  borderRadius={4}
                  mb={2}
                >
                  <HistoryIcon
                    style={{ fontSize: 20, marginRight: 8, color: '#aaa' }}
                  />
                  <Typography variant="body2" style={{ color: '#fff' }}>
                    Last Execution:{' '}
                    <strong>
                      {new Date(lastExecution.stop).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </strong>
                  </Typography>
                </Box>
              )}

              <Divider style={{ marginBottom: 16 }} />

              {isLoading ? (
                <Box style={{ textAlign: 'center', padding: 16 }}>
                  <CircularProgress />
                </Box>
              ) : getSchedulesByType('stop').length > 0 ? (
                getSchedulesByType('stop').map(schedule => {
                  const cronParts = formatCronParts(schedule.value);
                  const cronDescription = parseCronExpression(schedule.value);
                  return (
                    <Box
                      key={schedule.key}
                      className={`${classes.scheduleItem} ${classes.scheduleItemStop}`}
                    >
                      <Box style={{ flex: 1 }}>
                        <Typography className={classes.scheduleKey}>
                          {schedule.key.replace('cloudstart/', '')}
                        </Typography>
                        <Typography className={classes.cronExpression}>
                          {schedule.value}
                        </Typography>
                        <Typography className={classes.cronDescription}>
                          <AccessTimeIcon fontSize="small" />
                          {cronDescription}
                        </Typography>
                        {cronParts && (
                          <Box className={classes.cronParts}>
                            <Chip
                              size="small"
                              label={`M:${cronParts.minute}`}
                              style={{
                                backgroundColor: '#1565C0',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`H:${cronParts.hour}`}
                              style={{
                                backgroundColor: '#6A1B9A',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`D:${cronParts.dayOfMonth}`}
                              style={{
                                backgroundColor: '#EF6C00',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`Mo:${cronParts.month}`}
                              style={{
                                backgroundColor: '#00695C',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Chip
                              size="small"
                              label={`W:${cronParts.dayOfWeek}`}
                              style={{
                                backgroundColor: '#AD1457',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Box className={classes.actions}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenScheduleDialog(
                                KubernetesAction.UPDATE_SCHEDULER,
                                'stop',
                                schedule,
                              )
                            }
                            disabled={isActionInProgress}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenScheduleDialog(
                                KubernetesAction.DELETE_SCHEDULER,
                                'stop',
                                schedule,
                              )
                            }
                            disabled={isActionInProgress}
                          >
                            <DeleteIcon fontSize="small" color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box className={classes.emptyState}>
                  <Typography variant="body2">
                    No stop schedules configured
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <KubernetesScheduleDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onConfirm={handleScheduleConfirm}
        action={selectedAction}
        existingSchedules={schedules.map(s => ({ key: s.key, value: s.value }))}
        isLoading={isActionInProgress}
        initialScheduleType={dialogScheduleType}
        selectedSchedule={selectedSchedule}
      />

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelToggle}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isSchedulerEnabled ? 'Disable' : 'Enable'} Scheduler?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {isSchedulerEnabled ? 'disable' : 'enable'}{' '}
            the scheduler?
          </Typography>
          <Alert
            severity={isSchedulerEnabled ? 'warning' : 'info'}
            style={{ marginTop: 16 }}
          >
            <Typography variant="body2">
              {isSchedulerEnabled
                ? 'This will stop all scheduled start/stop operations. Resources will remain in their current state.'
                : 'This will activate all configured schedules. Resources will be started/stopped according to their schedule.'}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelToggle}
            color="default"
            disabled={isActionInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color="primary"
            variant="contained"
            disabled={isActionInProgress}
          >
            {isActionInProgress ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
